import {
  IonAlert,
  IonButton,
  IonCard,
  IonCardContent,
  IonIcon,
  IonProgressBar,
  IonText,
} from '@ionic/react';
import {
  checkmarkCircleOutline,
  closeCircleOutline,
  closeOutline,
  folder,
  pauseCircleOutline,
  playCircleOutline,
  refreshOutline,
  syncOutline,
} from 'ionicons/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useImportProgress } from '../../../context/ImportProgressContext';
import { on, once, removeListener, run } from '../../../helper/electron';
import { FolderStatusBadge } from './FolderStatusBadge';
import type { FolderListProps } from './types';
import { useIonToast } from '@ionic/react';

export default function FolderList({
  folders,
  selectedFolders,
  onSelectionChange,
  onRemoveFolder,
  onToggleWatching,
  onRescanFolder,
  allowBulkOperations = false,
  showAdvancedOptions = false,
  isImporting = false,
}: FolderListProps) {
  const { getJobsForPath, dispatch } = useImportProgress();
  const [present] = useIonToast();
  const [syncingFolders, setSyncingFolders] = useState<Set<string>>(new Set());
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  const [cancelJobId, setCancelJobId] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<
    Record<
      string,
      {
        filesFound: number;
        directoriesScanned: number;
        phase: string;
        lastUpdated: number;
      }
    >
  >({});

  useEffect(() => {
    const handler = (data: any) => {
      if (!data) return;
      const key = data.folderId || data.folderPath || 'unknown';
      setScanStatus((prev) => {
        //limit to 50 most recent folders
        const newStatus = { ...prev };
        const keys = Object.keys(newStatus);
        if (keys.length > 50) {
          // Remove oldest entries
          const sortedKeys = keys.sort(
            (a, b) =>
              (newStatus[a]?.lastUpdated || 0) -
              (newStatus[b]?.lastUpdated || 0),
          );
          sortedKeys
            .slice(0, keys.length - 50)
            .forEach((k) => delete newStatus[k]);
        }
        newStatus[key] = {
          filesFound: data.filesFound || 0,
          directoriesScanned: data.directoriesScanned || 0,
          phase: data.phase || 'scanning',
          lastUpdated: Date.now(),
        };
        return newStatus;
      });
    };
    on('my-music/scan-progress', handler);
    return () => {
      try {
        removeListener('my-music/scan-progress');
      } catch {}
      setScanStatus({});
    };
  }, []);

  const handleSyncFolder = useCallback(
    (folderPath: string, folderName: string) => {
      setSyncingFolders((prev) => new Set(prev).add(folderPath));
      present({
        message: `Syncing ${folderName}...`,
        duration: 2000,
        color: 'primary',
      });
      once('my-music/sync-folders-selected', (success: boolean) => {
        setSyncingFolders((prev) => {
          const next = new Set(prev);
          next.delete(folderPath);
          return next;
        });
        if (success) {
          present({
            message: `${folderName} synced successfully`,
            duration: 2000,
            color: 'success',
          });
          window.dispatchEvent(
            new CustomEvent('folder-manager/clear-changes', {
              detail: { path: folderPath },
            }),
          );
          window.dispatchEvent(
            new CustomEvent('library-refresh', {
              detail: { reason: 'folder-synced', folderPath },
            }),
          );
        } else {
          present({
            message: `Failed to sync ${folderName}`,
            duration: 3000,
            color: 'danger',
          });
        }
      });
      run('my-music/sync-folders-selected', { paths: [folderPath] });
    },
    [present],
  );

  const cancelStartTimesRef = useRef<Record<string, number>>({});
  const CANCEL_TIMEOUT_S = 15;
  const [cancelSecondsLeft, setCancelSecondsLeft] = useState<
    Record<string, number>
  >({});
  useEffect(() => {
    const cancellingJobs = folders.flatMap((f) =>
      getJobsForPath(f.path).filter((j) => j.status === 'cancelling'),
    );
    if (cancellingJobs.length === 0) return;

    const tick = () => {
      const now = Date.now();
      setCancelSecondsLeft((prev) => {
        const next = { ...prev };
        cancellingJobs.forEach((job) => {
          const start = cancelStartTimesRef.current[job.id] ?? now;
          const elapsedS = Math.floor((now - start) / 1000);
          next[job.id] = Math.max(CANCEL_TIMEOUT_S - elapsedS, 0);
        });
        return next;
      });
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [folders, getJobsForPath]);

  const [showPerformanceWarning, setShowPerformanceWarning] = useState(false);
  const [pendingWatchingToggle, setPendingWatchingToggle] = useState<{
    folderId: string;
    enabled: boolean;
  } | null>(null);

  const eventTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleSelectionChange = useCallback(
    (folderId: string, isSelected: boolean) => {
      const newSelected = new Set(selectedFolders);
      if (isSelected) {
        newSelected.add(folderId);
      } else {
        newSelected.delete(folderId);
      }
      onSelectionChange(newSelected);

      if (eventTimeoutRef.current) {
        clearTimeout(eventTimeoutRef.current);
      }
      eventTimeoutRef.current = setTimeout(() => {
        try {
          const selectedPaths = folders
            .filter((f) => newSelected.has(f.id))
            .map((f) => f.path);
          window.dispatchEvent(
            new CustomEvent('folder-manager/selection-changed', {
              detail: { selected: newSelected.size, paths: selectedPaths },
            }),
          );
        } catch {}
      }, 100);
    },
    [selectedFolders, folders, onSelectionChange],
  );
  useEffect(() => {
    return () => {
      if (eventTimeoutRef.current) {
        clearTimeout(eventTimeoutRef.current);
      }
    };
  }, []);

  const handleWatchingToggle = (folderId: string, enabled: boolean) => {
    if (enabled && !showPerformanceWarning) {
      // Show performance warning when enabling watching
      setPendingWatchingToggle({ folderId, enabled });
      setShowPerformanceWarning(true);
    } else {
      // Disable watching or user confirmed warning
      onToggleWatching?.(folderId, enabled);
    }
  };

  const confirmPerformanceWarning = () => {
    if (pendingWatchingToggle) {
      onToggleWatching?.(
        pendingWatchingToggle.folderId,
        pendingWatchingToggle.enabled,
      );
    }
    setShowPerformanceWarning(false);
    setPendingWatchingToggle(null);
  };

  const cancelPerformanceWarning = () => {
    setShowPerformanceWarning(false);
    setPendingWatchingToggle(null);
  };

  const getProgressColor = (status: string) => {
    switch (status) {
      case 'complete':
      case 'cancelled':
        return 'success';
      case 'error':
        return 'danger';
      case 'importing':
        return 'primary';
      case 'cancelling':
        return 'warning';
      case 'queued':
        return 'medium';
      default:
        return 'medium';
    }
  };

  const getStatusIcon = (status: string, hasError: boolean) => {
    if (hasError || status === 'error') return closeCircleOutline;
    if (status === 'complete' || status === 'cancelled')
      return checkmarkCircleOutline;
    if (status === 'importing') return playCircleOutline;
    if (status === 'queued') return pauseCircleOutline;
    return folder;
  };

  const formatMessage = (message: string, maxLength = 50) => {
    return message.length > maxLength
      ? `${message.substring(0, maxLength)}...`
      : message;
  };

  const handleStatusIconClick = (
    folderId: string,
    status: string,
    hasError: boolean,
  ) => {
    if (hasError || status === 'error') {
      // Retry/rescan on error
      if (onRescanFolder) {
        onRescanFolder(folderId);
      }
    } else if (status === 'complete' || status === 'cancelled') {
      // Rescan on complete or cancelled
      if (onRescanFolder) {
        onRescanFolder(folderId);
      }
    }
    // For importing/queued states, we don't provide any action
  };

  const getStatusText = (status: string, hasError: boolean) => {
    if (hasError || status === 'error') return 'Error';
    if (status === 'complete') return 'Complete';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'importing') return 'Importing';
    if (status === 'cancelling') return 'Cancelling…';
    if (status === 'queued') return 'Queued';
    return 'Ready';
  };

  return (
    <div className="folder-list">
      {folders.map((folderItem) => {
        const isSelected = selectedFolders.has(folderItem.id);
        const jobs = getJobsForPath(folderItem.path);
        const isSyncing = syncingFolders.has(folderItem.path);
        const sortedJobs = [...jobs].sort((a, b) => b.addedAt - a.addedAt);
        const currentJob =
          sortedJobs.find(
            (job) =>
              job.status === 'importing' ||
              job.status === 'queued' ||
              job.status === 'cancelling',
          ) || sortedJobs[0];
        const hasError = currentJob?.status === 'error';
        const isActiveImport =
          currentJob?.status === 'importing' ||
          currentJob?.status === 'cancelling';
        const isCancelling = currentJob?.status === 'cancelling';
        const isQueued = currentJob?.status === 'queued';
        const isPaused = ((currentJob as any)?.status as string) === 'paused';
        const progress = currentJob?.progress;
        let progressPercent =
          progress?.totalTracks > 0
            ? Math.floor(
                (progress.processedTracks / progress.totalTracks) * 100,
              )
            : 0;
        if (progress?.phase !== 'complete' && progressPercent === 100) {
          progressPercent = 99;
        }

        return (
          <IonCard
            key={folderItem.id}
            className={`folder-card compact ${getProgressColor(currentJob?.status || folderItem.status)} ${hasError ? 'folder-card--error' : ''}`}
          >
            <IonCardContent>
              <div className="folder-card__content">
                <div className="folder-card__main">
                  {/* Folder Name Line - Now at top */}
                  <div className="folder-card__title-line">
                    <input
                      type="checkbox"
                      className="folder-select"
                      checked={isSelected}
                      onChange={(e) =>
                        handleSelectionChange(folderItem.id, e.target.checked)
                      }
                      title={isSelected ? 'Deselect folder' : 'Select folder'}
                    />
                    <IonIcon icon={folder} className="folder-icon" />
                    <h3 className="folder-name">{folderItem.name}</h3>
                  </div>

                  {/* Status Line with Total Count */}
                  <div className="folder-card__status-line">
                    <IonIcon
                      icon={getStatusIcon(
                        currentJob?.status || folderItem.status,
                        hasError,
                      )}
                      color={getProgressColor(
                        currentJob?.status || folderItem.status,
                      )}
                      className={`status-icon ${hasError || (currentJob?.status || folderItem.status) === 'complete' || (currentJob?.status || folderItem.status) === 'cancelled' ? 'status-icon--clickable' : ''}`}
                      onClick={() =>
                        handleStatusIconClick(
                          folderItem.id,
                          currentJob?.status || folderItem.status,
                          hasError,
                        )
                      }
                      title={
                        hasError
                          ? `Click to retry import - ${currentJob?.error || folderItem.error || 'Unknown error'}`
                          : (currentJob?.status || folderItem.status) ===
                                'complete' ||
                              (currentJob?.status || folderItem.status) ===
                                'cancelled'
                            ? 'Click to rescan folder for new files'
                            : undefined
                      }
                    />
                    <IonText
                      color={getProgressColor(
                        currentJob?.status || folderItem.status,
                      )}
                      className="status-text"
                    >
                      {(currentJob?.status || folderItem.status) ===
                        'complete' ||
                      (currentJob?.status || folderItem.status) === 'cancelled'
                        ? (() => {
                            const totalTracks =
                              progress && (progress.created || progress.merged)
                                ? (progress.created || 0) +
                                  (progress.merged || 0)
                                : folderItem.trackCount || 0;
                            return `${totalTracks.toLocaleString()} tracks`;
                          })()
                        : getStatusText(
                            currentJob?.status || folderItem.status,
                            hasError,
                          )}
                    </IonText>
                  </div>

                  {/* Folder Path Line */}
                  <p className="folder-card__path">{folderItem.path}</p>
                  {/* Scanning line removed per UX preference; progress bar and stats below suffice */}
                </div>

                {/* Action Buttons */}
                <div className="folder-card__actions icon-only">
                  {folderItem.changes &&
                    (folderItem.changes.total > 0 ||
                      folderItem.changes.added > 0 ||
                      folderItem.changes.deleted > 0) && (
                      <div className="changes-box">
                        <FolderStatusBadge changes={folderItem.changes} />
                        <IonButton
                          fill="clear"
                          size="small"
                          color="primary"
                          onClick={() =>
                            handleSyncFolder(folderItem.path, folderItem.name)
                          }
                          className="sources-btn icon-btn"
                          title="Sync changes"
                          disabled={isSyncing}
                        >
                          <IonIcon icon={syncOutline} />
                          <span style={{ marginLeft: 6 }}>
                            {isSyncing ? 'Syncing...' : 'Sync'}
                          </span>
                        </IonButton>
                      </div>
                    )}

                  {(hasError ||
                    (currentJob?.status || folderItem.status) === 'complete' ||
                    (currentJob?.status || folderItem.status) ===
                      'cancelled') && (
                    <IonButton
                      fill="clear"
                      size="small"
                      color={getProgressColor(
                        currentJob?.status || folderItem.status,
                      )}
                      onClick={() =>
                        handleStatusIconClick(
                          folderItem.id,
                          currentJob?.status || folderItem.status,
                          hasError,
                        )
                      }
                      className="sources-btn icon-btn"
                      title="Rescan folder"
                    >
                      <IonIcon icon={refreshOutline} />
                      <span style={{ marginLeft: 6 }}>Rescan</span>
                    </IonButton>
                  )}

                  {/* Hide Remove while importing to prevent confusing mid-import deletes */}
                  {!isActiveImport && !isQueued && (
                    <IonButton
                      fill="clear"
                      size="small"
                      color="danger"
                      onClick={() => onRemoveFolder(folderItem.id)}
                      className={`sources-btn icon-btn ${(folderItem as any).deleting ? 'is-deleting' : ''}`}
                      title="Remove folder"
                      disabled={Boolean((folderItem as any).deleting)}
                    >
                      <IonIcon icon={closeOutline} />
                      <span style={{ marginLeft: 6 }}>
                        {(folderItem as any).deleting
                          ? `Removing…${(folderItem as any).deletedCount && (folderItem as any).toDelete ? ` ${(folderItem as any).deletedCount}/${(folderItem as any).toDelete}` : ''}`
                          : 'Remove'}
                      </span>
                    </IonButton>
                  )}

                  {/* Inline import controls — hide cancel once cancelling is in progress */}
                  {(isActiveImport || isQueued || isPaused) &&
                    currentJob &&
                    !isCancelling && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 6,
                          alignItems: 'center',
                        }}
                      >
                        <IonButton
                          fill="clear"
                          size="small"
                          color="danger"
                          onClick={() => setCancelJobId(currentJob.id)}
                          className="sources-btn icon-btn"
                          title="Cancel import"
                        >
                          <IonIcon icon={closeOutline} />
                          <span style={{ marginLeft: 6 }}>Cancel</span>
                        </IonButton>
                      </div>
                    )}
                </div>
              </div>

              {/* Show progress information */}
              {currentJob &&
                (isActiveImport || isQueued || isPaused || hasError) && (
                  <div className="folder-list__progress">
                    {isQueued && (
                      <IonText color="medium">
                        <p className="progress-message">
                          ⏳ Queued for import...
                        </p>
                      </IonText>
                    )}

                    {isActiveImport && (
                      <>
                        {isCancelling ? (
                          (() => {
                            const isCancelPhase =
                              progress?.phase === 'cancelling';
                            const cancelTotal =
                              isCancelPhase && progress.totalTracks > 0
                                ? progress.totalTracks
                                : 0;
                            const cancelDone = isCancelPhase
                              ? progress.processedTracks
                              : 0;
                            const cancelPct =
                              cancelTotal > 0
                                ? Math.min(
                                    Math.floor(
                                      (cancelDone / cancelTotal) * 100,
                                    ),
                                    99,
                                  )
                                : null;
                            const isRemovingTracks =
                              isCancelPhase && cancelTotal > 0;
                            const secsLeft = currentJob
                              ? (cancelSecondsLeft[currentJob.id] ??
                                CANCEL_TIMEOUT_S)
                              : CANCEL_TIMEOUT_S;
                            const countdownLabel = `${secsLeft}s`;

                            return (
                              <>
                                <IonText color="warning">
                                  <p className="progress-message">
                                    {isRemovingTracks
                                      ? `🗑️ Removing imported tracks… ${cancelPct}%`
                                      : `⏳ Cancelling… finishing current batch (${countdownLabel})`}
                                  </p>
                                </IonText>

                                {isRemovingTracks ? (
                                  <>
                                    <IonProgressBar
                                      value={cancelPct! / 100}
                                      color="warning"
                                      className="folder-progress-bar"
                                    />
                                    <IonText color="medium">
                                      <p className="progress-stats">
                                        {cancelDone.toLocaleString()} /{' '}
                                        {cancelTotal.toLocaleString()} tracks
                                        removed
                                      </p>
                                    </IonText>
                                  </>
                                ) : (
                                  <IonProgressBar
                                    type="indeterminate"
                                    color="warning"
                                    className="folder-progress-bar"
                                  />
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <>
                            <IonText color="primary">
                              <p className="progress-message">
                                {formatMessage(
                                  progress?.message || 'Importing...',
                                )}
                              </p>
                            </IonText>
                            {isPaused && (
                              <IonText color="medium">
                                <p
                                  className="progress-message"
                                  style={{ fontWeight: 600 }}
                                >
                                  ⏸️ Import paused
                                </p>
                              </IonText>
                            )}
                            {progress?.totalTracks > 0 && (
                              <>
                                <IonProgressBar
                                  value={progressPercent / 100}
                                  color="primary"
                                  className="folder-progress-bar"
                                />
                                <IonText color="medium">
                                  <p className="progress-stats">
                                    {progress.processedTracks} /{' '}
                                    {progress.totalTracks} tracks
                                    {(progress.created || 0) > 0 &&
                                      ` • ${progress.created} new`}
                                    {(progress.merged || 0) > 0 &&
                                      ` • ${progress.merged} updated`}
                                    {(() => {
                                      const stableDirs: number | undefined = (
                                        progress as any
                                      ).totalDirectories;
                                      if (typeof stableDirs === 'number') {
                                        return ` • ${progress.totalTracks.toLocaleString()} files in ${stableDirs.toLocaleString()} folders`;
                                      }
                                      const scan =
                                        scanStatus[folderItem.id] ||
                                        scanStatus[folderItem.path];
                                      return scan
                                        ? ` • ${progress.totalTracks.toLocaleString()} files in ${scan.directoriesScanned.toLocaleString()} folders`
                                        : '';
                                    })()}
                                  </p>
                                </IonText>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {hasError && (
                      <div className="folder-list__error">
                        <IonText color="danger">
                          <p className="error-title">
                            ❌ <strong>Import Failed</strong>
                          </p>
                          <p className="error-message">
                            {currentJob?.error ||
                              folderItem.error ||
                              'An unknown error occurred during import'}
                          </p>
                          <p className="error-action">
                            💡 Click the error icon above to retry
                          </p>
                        </IonText>
                      </div>
                    )}
                  </div>
                )}
            </IonCardContent>
          </IonCard>
        );
      })}

      {/* Performance Warning Alert */}
      <IonAlert
        isOpen={showPerformanceWarning}
        onDidDismiss={cancelPerformanceWarning}
        header="Performance Impact Warning"
        message="Enabling file watching for this folder will monitor for new music files automatically. This may impact performance, especially on large folders or slower systems. You can disable this feature at any time."
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            handler: cancelPerformanceWarning,
          },
          {
            text: 'Enable Anyway',
            handler: confirmPerformanceWarning,
          },
        ]}
      />

      {/* Cancel Import Confirmation */}
      <IonAlert
        isOpen={cancelJobId !== null}
        onDidDismiss={() => setCancelJobId(null)}
        header="Cancel import?"
        message="Do you want to remove tracks already imported from this run, or keep them?"
        buttons={[
          {
            text: 'Remove imported',
            role: 'destructive',
            handler: () => {
              if (cancelJobId) {
                cancelStartTimesRef.current[cancelJobId] = Date.now();
                window.dispatchEvent(
                  new CustomEvent('track-import/cancel', {
                    detail: { id: cancelJobId, removeImported: true },
                  }),
                );
                dispatch({
                  type: 'CANCELLING_JOB',
                  payload: { id: cancelJobId },
                });
              }
              setCancelJobId(null);
            },
          },
          {
            text: 'Keep imported',
            handler: () => {
              if (cancelJobId) {
                cancelStartTimesRef.current[cancelJobId] = Date.now();
                window.dispatchEvent(
                  new CustomEvent('track-import/cancel', {
                    detail: { id: cancelJobId, removeImported: false },
                  }),
                );
                dispatch({
                  type: 'CANCELLING_JOB',
                  payload: { id: cancelJobId },
                });
              }
              setCancelJobId(null);
            },
          },
          {
            text: 'Close',
            role: 'cancel',
            handler: () => setCancelJobId(null),
          },
        ]}
      />
    </div>
  );
}
